"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.withholdingReportResponse = exports.arAgingResponse = exports.customerStatementResponse = exports.receiptApplicationListQuery = exports.receiptApplicationDto = exports.createReceiptApplicationRequest = exports.receiptListQuery = exports.customerReceiptDto = exports.createCustomerReceiptRequest = exports.receiptWithholdingInput = exports.receiptAllocationInput = void 0;
const zod_1 = require("zod");
const ledger_1 = require("./ledger");
const common_1 = require("./common");
const positiveMoney = common_1.moneyString.refine((value) => !value.startsWith('-') && /[1-9]/.test(value), 'amount must be positive');
exports.receiptAllocationInput = zod_1.z
    .object({
    invoiceId: common_1.uuid,
    amount: positiveMoney,
})
    .strict();
/**
 * الخصم من المنبع على التحصيل: مبلغ، أو نسبة على وعاء، أو الثلاثة متطابقة. النسبة
 * يدخلها المستخدم أو يختارها من إعدادات شركته — لا نسبة قانونية فى الكود (`BLOCKED-TAX`).
 */
exports.receiptWithholdingInput = zod_1.z
    .object({
    amount: positiveMoney.optional(),
    ratePercent: positiveMoney.optional(),
    base: positiveMoney.optional(),
})
    .strict();
/**
 * تحصيل من عميل. التخصيص على الفواتير صريح؛ ما لم يُخصص دفعة مقدمة تُسوّى لاحقًا،
 * مكانها حسب `settings.sales.customerAdvances`. «الأقدم أولاً» اقتراح فى الواجهة لا قاعدة
 * فى الخادم. التخصيصات حتى (النقد + الخصم من المنبع).
 */
exports.createCustomerReceiptRequest = zod_1.z
    .object({
    customerId: common_1.uuid,
    receiptDate: common_1.isoDate,
    /** النقد المقبوض فعلاً. */
    amount: positiveMoney,
    /** حساب خزينة أو بنك أو محفظة طرفى — المقبوض فيه. */
    depositAccountCode: common_1.accountCode,
    externalReference: zod_1.z.string().trim().max(64).optional(),
    memo: zod_1.z.string().trim().max(500).optional(),
    allocations: zod_1.z.array(exports.receiptAllocationInput).max(200).default([]),
    withholding: exports.receiptWithholdingInput.optional(),
})
    .strict();
exports.customerReceiptDto = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    number: zod_1.z.string(),
    customerId: common_1.uuid,
    customerName: zod_1.z.string(),
    receiptDate: common_1.isoDate,
    amount: common_1.moneyString,
    depositAccountCode: common_1.accountCode,
    externalReference: zod_1.z.string().nullable(),
    memo: zod_1.z.string().nullable(),
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
    /** ما خصمه العميل من المنبع — يسدد الذمة كالنقد. */
    withholdingAmount: common_1.moneyString,
    /** أين رُحّل الباقى غير المخصص وقت الإيصال. */
    advanceMode: zod_1.z.enum(['separate_account', 'customer_balance']),
    /** التخصيصات مع الإيصال (`applicationId` فارغ) أو بتسويات لاحقة. */
    allocations: zod_1.z.array(zod_1.z.object({
        invoiceId: common_1.uuid,
        invoiceNumber: zod_1.z.string(),
        amount: common_1.moneyString,
        applicationId: common_1.uuid.nullable(),
    })),
    /** الباقى دفعة مقدمة لم تُسوَّ بعد: (النقد + الخصم) − كل التخصيصات. */
    unallocated: common_1.moneyString,
});
exports.receiptListQuery = zod_1.z.object({ customerId: common_1.uuid.optional() }).strict();
/** تسوية دفعة مقدمة (باقى إيصال غير مخصص) على فاتورة أو أكثر صدرت بعده. */
exports.createReceiptApplicationRequest = zod_1.z
    .object({
    receiptId: common_1.uuid,
    applicationDate: common_1.isoDate,
    memo: zod_1.z.string().trim().max(500).optional(),
    allocations: zod_1.z.array(exports.receiptAllocationInput).min(1).max(200),
})
    .strict();
exports.receiptApplicationDto = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    number: zod_1.z.string(),
    receiptId: common_1.uuid,
    receiptNumber: zod_1.z.string(),
    customerId: common_1.uuid,
    customerName: zod_1.z.string(),
    applicationDate: common_1.isoDate,
    amount: common_1.moneyString,
    memo: zod_1.z.string().nullable(),
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
    allocations: zod_1.z.array(zod_1.z.object({ invoiceId: common_1.uuid, invoiceNumber: zod_1.z.string(), amount: common_1.moneyString })),
});
exports.receiptApplicationListQuery = zod_1.z
    .object({ customerId: common_1.uuid.optional(), receiptId: common_1.uuid.optional() })
    .strict();
/**
 * كشف حساب عميل: سطور حساب العملاء وحساب الدفعات المقدمة الحاملة لمعرّفه، بنفس قواعد
 * كشف الحساب (افتتاحى = ما قبل البداية، رصيد جارٍ، مدين موجب). الدفعة المقدمة دائنة
 * فتُخفض الرصيد، وتسويتها تنقل بين الحسابين فلا تغيّره. قيد يدوى بلا معرّف عميل لا
 * يظهر هنا — ويظهر فرقًا فى مطابقة أعمار الديون.
 */
exports.customerStatementResponse = zod_1.z.object({
    companyId: common_1.uuid,
    customer: zod_1.z.object({ id: common_1.uuid, code: zod_1.z.string(), nameAr: zod_1.z.string() }),
    accountCode: common_1.accountCode,
    /** حساب الدفعات المقدمة المضموم للكشف. */
    advanceAccountCode: common_1.accountCode,
    from: common_1.isoDate,
    to: common_1.isoDate,
    openingBalance: common_1.moneyString,
    lines: zod_1.z.array(ledger_1.accountLedgerLine.extend({ accountCode: common_1.accountCode })),
    totalDebit: common_1.moneyString,
    totalCredit: common_1.moneyString,
    closingBalance: common_1.moneyString,
});
const agingAmounts = {
    current: common_1.moneyString,
    days1to30: common_1.moneyString,
    days31to60: common_1.moneyString,
    days61to90: common_1.moneyString,
    over90: common_1.moneyString,
    unappliedCredit: common_1.moneyString,
    total: common_1.moneyString,
};
/**
 * أعمار ديون العملاء فى تاريخ، مع **مطابقتها بالحساب الرقابى**: رصيد حساب العملاء فى
 * الدفتر مقابل مجموع الأعمار. الفرق ليس خطأ حساب بل قيود على حساب العملاء لا تخص
 * فاتورة أو تحصيلاً (يدوية أو افتتاحية) — يُعرض صريحًا ولا يُخفى.
 */
exports.arAgingResponse = zod_1.z.object({
    companyId: common_1.uuid,
    asOf: common_1.isoDate,
    rows: zod_1.z.array(zod_1.z.object({ customerId: common_1.uuid, code: zod_1.z.string(), nameAr: zod_1.z.string(), ...agingAmounts })),
    totals: zod_1.z.object(agingAmounts),
    controlAccountCode: common_1.accountCode,
    /** الدفعات المقدمة دائن غير مخصص — رصيدها داخل `controlBalance` مع حساب العملاء. */
    advanceAccountCode: common_1.accountCode,
    controlBalance: common_1.moneyString,
    unreconciled: common_1.moneyString,
});
/**
 * الخصم من المنبع لحسابنا فى فترة — للتسوية مع المصلحة. من سطور حساب ضرائب الخصم
 * فى الدفتر نفسه لا من جدول موازٍ: ما يُحمّل بقيد يدوى أو يُسوّى مع المصلحة يظهر هنا.
 */
exports.withholdingReportResponse = zod_1.z.object({
    companyId: common_1.uuid,
    accountCode: common_1.accountCode,
    from: common_1.isoDate,
    to: common_1.isoDate,
    /** رصيد الحساب قبل الفترة (مدين موجب). */
    openingBalance: common_1.moneyString,
    byCustomer: zod_1.z.array(zod_1.z.object({
        customerId: common_1.uuid.nullable(),
        code: zod_1.z.string().nullable(),
        nameAr: zod_1.z.string().nullable(),
        debit: common_1.moneyString,
        credit: common_1.moneyString,
    })),
    lines: zod_1.z.array(zod_1.z.object({
        journalId: common_1.uuid,
        journalNumber: zod_1.z.string().nullable(),
        postingDate: common_1.isoDate,
        reference: zod_1.z.string().nullable(),
        customerId: common_1.uuid.nullable(),
        customerName: zod_1.z.string().nullable(),
        ratePercent: zod_1.z.string().nullable(),
        base: common_1.moneyString.nullable(),
        debit: common_1.moneyString,
        credit: common_1.moneyString,
    })),
    totalDebit: common_1.moneyString,
    totalCredit: common_1.moneyString,
    closingBalance: common_1.moneyString,
});
//# sourceMappingURL=receivables.js.map